import type { LiveMatch } from "@/lambda/sams/types";

type TeamSide = "team1" | "team2";

export type LiveTickerDisplayMatch = {
  matchUuid: string;
  team1Uuid: string;
  team2Uuid: string;
  team1Name: string;
  team2Name: string;
  setPointsText: string;
  team1SetPoints: number;
  team2SetPoints: number;
  setScores: Array<{
    setNumber: number;
    team1Score: number;
    team2Score: number;
  }>;
  activeSetNumber: number | null;
  isFinished: boolean;
  weAreWinning: boolean;
};

/** Primary (large) and optional secondary (small) scores for the live card center. */
export function getLiveTickerScoreDisplay(match: LiveTickerDisplayMatch): {
  primaryText: string;
  secondaryText: string | null;
} {
  const activeSet = match.activeSetNumber
    ? match.setScores.find((setScore) => setScore.setNumber === match.activeSetNumber)
    : undefined;

  // Ongoing: emphasize points in the current set; sets sit underneath.
  if (!match.isFinished && activeSet) {
    return {
      primaryText: `${activeSet.team1Score}:${activeSet.team2Score}`,
      secondaryText: match.setPointsText,
    };
  }

  // Finished (or no active set yet): only show set score.
  return {
    primaryText: match.setPointsText,
    secondaryText: null,
  };
}

export function toLiveTickerDisplayMatches(args: {
  liveMatches: LiveMatch[];
  ourTeamUuids: Set<string>;
}): LiveTickerDisplayMatch[] {
  const { liveMatches, ourTeamUuids } = args;

  const result: LiveTickerDisplayMatch[] = [];

  for (const match of liveMatches) {
    const ourSide = getOurSide(match, ourTeamUuids);
    if (!ourSide) continue;

    const otherSide: TeamSide = ourSide === "team1" ? "team2" : "team1";
    const team1SetPoints = match.state.setPoints.team1;
    const team2SetPoints = match.state.setPoints.team2;
    const setPointsText = `${match.state.setPoints.team1}:${match.state.setPoints.team2}`;
    const setScores = [...match.state.matchSets]
      .sort((a, b) => a.setNumber - b.setNumber)
      .map((setItem) => ({
        setNumber: setItem.setNumber,
        team1Score: setItem.setScore.team1,
        team2Score: setItem.setScore.team2,
      }));
    const activeSetNumber =
      !match.state.finished && setScores.length > 0
        ? (setScores[setScores.length - 1]?.setNumber ?? null)
        : null;

    const team1Name = match.team1Name;
    const team2Name = match.team2Name;
    result.push({
      matchUuid: match.matchUuid,
      team1Uuid: match.team1Uuid,
      team2Uuid: match.team2Uuid,
      team1Name,
      team2Name,
      setPointsText,
      team1SetPoints,
      team2SetPoints,
      setScores,
      activeSetNumber,
      isFinished: match.state.finished,
      weAreWinning: match.state.setPoints[ourSide] > match.state.setPoints[otherSide],
    });
  }

  return result;
}

function getOurSide(match: LiveMatch, ourTeamUuids: Set<string>): TeamSide | null {
  if (ourTeamUuids.has(match.team1Uuid)) return "team1";
  if (ourTeamUuids.has(match.team2Uuid)) return "team2";
  return null;
}
